import { gql, useApolloClient } from "@apollo/client";
import classNames from "classnames";
import SubpagesFilterDropdown from "components/FilterDropdown/SubpagesFilterDropdown";
import initNetYieldChart from "components/NetYieldChart";
import { BetterAlert as Alert } from "components/profile/Alert";
import React, { useMemo, useRef, useState } from "react";
import { Link, Redirect, Route, Switch, useHistory } from "react-router-dom";

import { SecondaryButton } from "~/components/common/buttons";
import { PencilIcon, TrashIcon } from "~/components/common/icons";
import { DeleteSubpageDialog } from "~/components/Overview/DeleteSubpageDialog";
import {
  FundEntityOverviewFragment,
  SubpageOverviewFragment,
  useDeleteSubpageMutation,
} from "~/components/Overview/OverviewPage.generated";
import {
  SUBPAGE_MENU_FRAGMENT,
  SubpagesMenu,
} from "~/components/Overview/SubpagesMenu";
import { UpdateSubpageDialog } from "~/components/Overview/UpdateSubpageDialog";

import Subpage from "./Subpage";

export const DELETE_SUBPAGE_MUTATION = gql`
  mutation DeleteSubpage($subpageId: ID!, $subjectType: String!) {
    deleteSubpage(subpageId: $subpageId, subjectType: $subjectType) {
      success
    }
  }
`;

export const SUBPAGE_OVERVIEW_FRAGMENT = gql`
  fragment SubpageOverview on SubpageInterface {
    id
    slug
    title
    description
    restricted
    roles {
      id
      name
    }
    groups {
      id
      name
    }
  }
`;

export const FUND_ENTITY_OVERVIEW_FRAGMENT = gql`
  fragment FundEntityOverview on FundEntity {
    id
    name
    hasSubpages
    subpagePermissions
    subpages {
      ...SubpageOverview
      ...SubpagesMenu
    }
    netYields {
      id
      year
      actualEnergy
      p50
      p90
    }
    projects {
      id
      name
      capacity
      type
      location {
        latitude
        longitude
      }
    }
    org {
      slug
    }
  }

  ${SUBPAGE_OVERVIEW_FRAGMENT}
  ${SUBPAGE_MENU_FRAGMENT}
`;

interface OverviewPageProps {
  subject: FundEntityOverviewFragment;
  isDealRoomAdmin: boolean;
  onViewSubpage: (subpage: SubpageOverviewFragment) => void;
}

export const OverviewPage = ({
  subject,
  isDealRoomAdmin,
  onViewSubpage,
}: OverviewPageProps) => {
  const client = useApolloClient();
  const history = useHistory();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [deleteSubpage, { loading: deleteLoading, error: deleteError }] =
    useDeleteSubpageMutation({
      onCompleted: (data) => {
        setShowDeleteDialog(false);
        client.cache.modify({
          id: client.cache.identify(subject), // Identify the subject in the cache
          fields: {
            subpages(existingSubpages = [], { readField }) {
              return existingSubpages.filter(
                (subpageRef: SubpageOverviewFragment) =>
                  readField("slug", subpageRef) !== currentSubpageSlug
              );
            },
          },
        });
        setShowDeleteDialog(false);
        if (data?.deleteSubpage?.success) {
          if (nextPageSlug) {
            history.push(`/overview/${nextPageSlug}`);
          } else if (previousPageSlug) {
            history.push(`/overview/${previousPageSlug}`);
          } else {
            history.push("/overview");
          }
        }
      },
    });
  const [isSubpageEditing, setIsSubpageEditing] = useState(false);
  const projectGeoJson = useMemo(() => {
    const projects = (subject?.projects ?? []).filter(
      (p) => p.location.latitude && p.location.longitude
    );
    return {
      type: "projects",
      features: projects.map((project) => {
        return {
          type: "project",
          properties: {
            name: project.name,
            size: project.capacity,
          },
          geometry: {
            type: "Point",
            coordinates: [
              project.location.longitude,
              project.location.latitude,
            ],
          },
        };
      }),
    };
  }, [subject]);
  const parentNodeRef = useRef(null);

  if (!subject.subpages?.length && !isDealRoomAdmin) {
    let message = `Please contact your administrator for access to details for ${subject.name}.`;
    if (!subject.hasSubpages)
      message = `Overview is missing. Click on "Documents" to access the dataroom for ${subject.name}.`;
    return <Alert message={`${message}`} classNames={["alert-warning"]} />;
  }

  const firstSubpage = subject.subpages?.length ? subject.subpages[0] : null;
  const trimmedPath = history.location.pathname.replace(/\/$/, ""); // Remove trailing slash
  const currentSubpageSlug = trimmedPath.substr(
    trimmedPath.lastIndexOf("/") + 1
  );

  const subpageSlugs = subject.subpages?.map((subpage) => subpage.slug);
  const currentIndex = subpageSlugs?.indexOf(currentSubpageSlug);
  const previousPageSlug =
    currentIndex !== undefined && currentIndex > 0 && subpageSlugs
      ? subpageSlugs[currentIndex - 1]
      : null;
  const nextPageSlug =
    currentIndex !== undefined &&
    subpageSlugs !== undefined &&
    currentIndex < subpageSlugs.length - 1
      ? subpageSlugs[currentIndex + 1]
      : null;

  const subpageChoices = [];
  if (subject.subpages) {
    for (let i = 0; i < subject.subpages.length; i++) {
      subpageChoices.push({
        key: subject.subpages[i].slug,
        value: subject.subpages[i].title,
        item: subject.subpages[i],
      });
    }
  }

  if (subject.netYields?.length) {
    // Not a React component, need to initialize google charts
    initNetYieldChart("net-yield-chart", subject.netYields);
  }
  const currentSubpage = subject.subpages?.find(
    (subpage) => subpage.slug === currentSubpageSlug
  );

  const handleConfirmDelete = async () => {
    if (!currentSubpage) return;

    try {
      await deleteSubpage({
        variables: {
          subpageId: currentSubpage.id,
          subjectType: subject.__typename,
        },
      });
    } catch (error) {
      console.error("Error deleting subpage:", error);
    }
  };

  return (
    <div>
      <div className="row mb-4" ref={parentNodeRef}>
        <div className="col-lg-9 overflow-x subpage-markdown">
          {firstSubpage ? (
            <Switch>
              {subject.subpages?.map((subpage) => (
                <Route
                  key={`r-${subpage.id}`}
                  path={`/overview/${subpage.slug}`}
                  render={() => (
                    <Subpage
                      subject={subject}
                      projectsGeoJson={projectGeoJson}
                      subpage={subpage}
                      onView={onViewSubpage}
                      isEditing={isSubpageEditing}
                      setIsEditing={setIsSubpageEditing}
                    />
                  )}
                />
              ))}
              <Redirect to={`/overview/${firstSubpage.slug}`} />
            </Switch>
          ) : (
            <div className="text-center">
              <Alert
                message="No subpages available. Please create a subpage."
                classNames={["alert-warning"]}
              />
            </div>
          )}
        </div>
        <div className="col-lg-3 subpage-table-of-contents">
          <SubpagesMenu
            currentSubpageSlug={currentSubpageSlug}
            subject={subject}
            subpages={subject.subpages}
            parentNodeRef={parentNodeRef}
            isSubpageEditing={isSubpageEditing}
            isDealRoomAdmin={isDealRoomAdmin}
          />
        </div>
      </div>
      <hr />
      {firstSubpage && (
        <>
          <div className="d-flex align-items-center">
            <SubpagesFilterDropdown
              selectedPage={currentSubpageSlug}
              items={subpageChoices}
              title="Table of Contents"
              onSelected={(selected) => {
                if (selected[0] !== currentSubpageSlug) {
                  history.push(selected[0]);
                }
              }}
              disabled={isSubpageEditing}
            />
            {isDealRoomAdmin && (
              <SecondaryButton
                onClick={() => setIsSubpageEditing(!isSubpageEditing)}
                className="ml-2"
                disabled={isSubpageEditing}
              >
                <PencilIcon />
                <span>{isSubpageEditing ? "Editing..." : "Edit Content"}</span>
              </SecondaryButton>
            )}
            {isDealRoomAdmin && (
              <SecondaryButton
                onClick={() => setShowUpdateDialog(true)}
                className="ml-2"
                disabled={isSubpageEditing}
              >
                <PencilIcon />
                <span>{isSubpageEditing ? "Editing..." : "Edit Subpage"}</span>
              </SecondaryButton>
            )}
            {isDealRoomAdmin && currentIndex !== undefined && (
              <SecondaryButton
                danger
                className="ml-2"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isSubpageEditing}
              >
                <TrashIcon />
                <span>Delete Subpage</span>
              </SecondaryButton>
            )}
          </div>
          <div className="d-flex mt-2">
            <div className="subpage-pagination ml-auto">
              <Link
                to={`/overview/${previousPageSlug}`}
                className={classNames(
                  "btn",
                  "btn-outline-primary",
                  "btn-icon",
                  "previous-page",
                  "custom-margin-right",
                  {
                    disabled: isSubpageEditing || previousPageSlug === null,
                  }
                )}
              />
              <Link
                to={`/overview/${nextPageSlug}`}
                className={classNames(
                  "btn",
                  "btn-outline-primary",
                  "btn-icon",
                  "next-page",
                  {
                    disabled: isSubpageEditing || nextPageSlug === null,
                  }
                )}
              />
            </div>
          </div>
        </>
      )}
      <DeleteSubpageDialog
        isVisible={showDeleteDialog}
        subpageTitle={
          subject.subpages?.find(
            (subpage) => subpage.slug === currentSubpageSlug
          )?.title || ""
        }
        onClose={() => setShowDeleteDialog(false)}
        onConfirmDelete={handleConfirmDelete}
        loading={deleteLoading}
        error={deleteError?.message}
      />
      {currentSubpage && (
        <UpdateSubpageDialog
          isVisible={showUpdateDialog}
          orgSlug={subject.org.slug}
          subpage={currentSubpage}
          subject={subject}
          loading={false}
          onClose={() => setShowUpdateDialog(false)}
        />
      )}
    </div>
  );
};
